import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download } from "lucide-react";

const EMOJI = { 1: "🙁", 2: "😐", 3: "🙂" };

export default function PortalReportDetail() {
    const { id } = useParams();
    const [r, setR] = useState(null);
    useEffect(() => { api.get(`/reports/${id}`).then((res) => setR(res.data)); }, [id]);
    if (!r) return <div>Cargando...</div>;
    const checks = (data, items) => items.map((it) => (
        <div key={it.k} className="flex justify-between text-sm py-1.5 border-b last:border-0">
            <span>{it.label}</span>
            <span className={data[it.k] === true ? "text-green-600 font-semibold" : data[it.k] === false ? "text-red-600 font-semibold" : "text-muted-foreground"}>
                {data[it.k] === true ? "Sí" : data[it.k] === false ? "No" : "—"}
            </span>
        </div>
    ));
    const EXT = [
        { k: "grass_cut", label: "Césped cortado" },
        { k: "drains_good", label: "Drenajes y sifones en buen estado" },
        { k: "machinery_organized", label: "Maquinaria organizada" },
        { k: "debris_organized", label: "Escombros ordenados" },
        { k: "grate_screens", label: "Zaranda en tragantes" },
        { k: "trash_containers_closed", label: "Contenedores cerrados" },
        { k: "waste_management", label: "Manejo de desperdicios" },
        { k: "nesting_sites_found", label: "Anidación encontrada" },
    ];
    return (
        <div data-testid="portal-report-detail">
            <Link to="/portal/reports" className="inline-flex items-center text-sm text-muted-foreground mb-3"><ArrowLeft className="w-4 h-4 mr-1" /> Reportes</Link>
            <Card className="p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Reporte de servicio</h1>
                        <div className="font-mono text-muted-foreground text-sm mt-1">{r.service_date} • {r.dte_invoice_number}</div>
                    </div>
                    <a href={assetUrl(r.report_pdf_url)} target="_blank" rel="noreferrer"><Button className="bg-[hsl(var(--trulyn-navy))] text-white"><Download className="w-4 h-4 mr-1" /> PDF</Button></a>
                </div>
                <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
                    <div><span className="text-muted-foreground">Entrada:</span> <span className="font-mono">{(r.entry_time || "—").slice(0, 16).replace("T", " ")}</span></div>
                    <div><span className="text-muted-foreground">Salida:</span> <span className="font-mono">{(r.exit_time || "—").slice(0, 16).replace("T", " ")}</span></div>
                    <div><span className="text-muted-foreground">Recibido por:</span> <span className="font-medium">{r.accepted_by_name || "—"}</span></div>
                </div>

                <h3 className="font-semibold mt-4 mb-2">Condiciones exteriores</h3>
                {checks(r.exterior || {}, EXT)}

                {r.recommendations && <div className="mt-4"><div className="text-xs text-muted-foreground">Recomendaciones</div><p className="text-sm">{r.recommendations}</p></div>}
                {r.areas_treated && <div className="mt-2"><div className="text-xs text-muted-foreground">Áreas tratadas</div><p className="text-sm">{r.areas_treated}</p></div>}

                {r.products_used?.length > 0 && (
                    <>
                        <h3 className="font-semibold mt-4 mb-2">Productos utilizados</h3>
                        <div className="space-y-1">
                            {r.products_used.map((p, i) => (
                                <div key={i} className="flex flex-wrap gap-2 items-center text-sm border rounded-md px-3 py-2">
                                    <span className="font-medium">{p.product_name}</span>
                                    <Badge variant="outline">{p.method}</Badge>
                                    <span className="font-mono text-muted-foreground">{p.quantity} {p.dose}</span>
                                    {p.pest_target && <span className="text-muted-foreground ml-auto">{p.pest_target}</span>}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {r.survey && (
                    <>
                        <h3 className="font-semibold mt-4 mb-2">Encuesta de satisfacción</h3>
                        <div className="grid grid-cols-5 gap-2 text-center text-sm">
                            {[
                                { k: "comments_rating", l: "Comentarios" },
                                { k: "punctuality_rating", l: "Puntualidad" },
                                { k: "friendliness_rating", l: "Amabilidad" },
                                { k: "cordiality_rating", l: "Cordialidad" },
                                { k: "efficacy_rating", l: "Eficacia" },
                            ].map((q) => (
                                <div key={q.k} className="border rounded-lg p-2">
                                    <div className="text-2xl">{EMOJI[r.survey[q.k]]}</div>
                                    <div className="text-xs text-muted-foreground">{q.l}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {r.photos?.length > 0 && (
                    <>
                        <h3 className="font-semibold mt-4 mb-2">Evidencia fotográfica</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="portal-report-photos">
                            {r.photos.map((ph, i) => (
                                <a key={ph.id || i} href={assetUrl(ph.file_url)} target="_blank" rel="noreferrer" className="block group">
                                    <div className="aspect-video bg-black/5 rounded-lg overflow-hidden border">
                                        <img src={assetUrl(ph.file_url)} alt={ph.caption || `Foto ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                                    </div>
                                    {ph.caption && <p className="text-xs text-muted-foreground mt-1 leading-snug line-clamp-2">{ph.caption}</p>}
                                </a>
                            ))}
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}
